import type { PlayerStatsWithId } from '@frontend/core/lib/domains/reporting/types'
import type { TabDef, TabGroupDef } from '../../../lib/sport-config'

// ---------------------------------------------------------------------------
// Accessor → presence-key mapping
// ---------------------------------------------------------------------------
//
// The all-players summary carries a per-player `metricsPresent: string[]` of
// backend `PlayerMetricSummary` json names. To intersect that signal against a
// tab's `TabDef.columns`, each metric column `accessor` must resolve to the
// backend presence key — or to `null` when the v2 pipeline does not yet emit it.
//
// Accessors equal their backend key, so the mapping is identity; the only
// special case is deferred metrics (below), which resolve to `null`. The unit
// tests in `player-availability.test.ts` guard the label/metric column separation.

/**
 * FE accessors whose metric the v2 pipeline does not yet emit in the presence
 * signal (backend deferral — soccer defensive/GK/duels/set-pieces, AMF route/
 * technical-load). They have no presence key, so a tab made up entirely of
 * these resolves to "show all" until the backend pipeline work lands.
 */
export const DEFERRED_ACCESSORS = new Set<string>([
  // soccer — defensive actions. The v2 pipeline does not emit these yet, so the
  // `defensive_actions` / `out_of_possession` tabs must fall back to show-all
  // rather than being wrongly hidden when treated as live identity keys.
  'defensiveActionCount',
  'defensiveActionCountP90',
  'defensiveActionRuns',
  'defensiveActionWorkload_J',
  'defensiveActionWorkRate_Js',
  // soccer — defensive
  'clearances',
  'blocks',
  'interceptions',
  // soccer — tackles
  'tackleCount',
  'tacklesWonPct',
  // soccer — duels
  'duelCount',
  'duelRuns',
  'duelWonPct',
  'duelWorkload_J',
  'duelWorkRate_Js',
  // soccer — goalkeeping
  'gkCount',
  'gkCountP90',
  'gkDiveCount',
  'gkDiveReturnCount',
  'gkExplosiveImpulse_Ns',
  'gkImpactImpulse_Ns',
  'gkSuccessPct',
  // soccer — pressure
  'pressureCount',
  'pressureRunsPerMin',
  'pressureRunWorkload_J',
  'pressureRunWorkRate_Js',
  // soccer — off-the-ball
  'offTheBallRuns',
  'offTheBallRunsPerMin',
  'offTheBallWorkload_J',
  'offTheBallWorkRate_Js',
  // soccer — take-ons
  'takeOnCount',
  'takeOnSuccessPct',
  // AMF
  'routeCount',
  'technicalLoad',
  'technicalLoadPerMin'
])

/**
 * Resolves a tab column `accessor` to its backend `metricsPresent` key: identity
 * (the accessor IS the backend key), or `null` when the metric is not yet emitted
 * (deferred). An FE accessor whose name can't equal its backend key must be
 * renamed to the backend key at the config + core-type level — never bridged only
 * here, or the value lookup (`getStatByAccessor`) reads the wrong field and the
 * column renders '-' while presence reports it as available.
 *
 * Assumes `accessor` is a metric column. Label/identity columns (player name,
 * period, …) live in each section's `staticColumns`, never in a tab's `columns`,
 * so they never reach here — enforced by the "labels never appear in tab metric
 * columns" invariant test in `player-availability.test.ts`.
 */
export const accessorToPresenceKey = (accessor: string): string | null =>
  DEFERRED_ACCESSORS.has(accessor) ? null : accessor

// ---------------------------------------------------------------------------
// Presence derivation
// ---------------------------------------------------------------------------

/** Per-player set of present backend metric keys. */
export type PresenceByPlayer = Record<string, Set<string>>

/**
 * Builds `presenceByPlayer` from the all-players summary `playerStats[]`.
 *
 * Sourced from `playerStats[]` directly — NOT the hook's `map`, which copies
 * only `entry.stats` and drops `metricsPresent`.
 *
 * A player is only given an entry when `metricsPresent` is an array (including
 * an empty one). Anything else — `undefined` (older BE / still loading) or a
 * defensive `null` from the JSON boundary that the optional type doesn't model
 * — leaves the player without an entry, which downstream predicates treat as
 * "show all". This is deliberate: absent ≠ `[]`. An empty array means "tracked,
 * has data for nothing" (hide), whereas absent/null means "unknown" (show all).
 */
export const buildPresenceByPlayer = (
  playerStats: PlayerStatsWithId[]
): PresenceByPlayer => {
  const result: PresenceByPlayer = {}
  for (const entry of playerStats) {
    if (Array.isArray(entry.metricsPresent)) {
      result[entry.playerId] = new Set(entry.metricsPresent)
    }
  }
  return result
}

/**
 * Per-player `hasEventData` flags from the all-players summary.
 *
 * `metricsPresent` and `hasEventData` come from separate, independently
 * populated backend pipelines (aggregated metrics vs. the raw event tree) —
 * a player can have real pitch-map events with an entirely empty
 * `metricsPresent`. This is intentionally coarse (a
 * session-wide flag, not per-tab): it exists only to stop a player who has
 * *some* data from making a whole tab group look "dead" — see
 * `hasEventData` usage in `player-detail/index.tsx`'s `noGroupData`. It must
 * not be folded into per-tab `hasTabData`, which is specifically about
 * metric-column presence.
 */
export const buildEventDataByPlayer = (
  playerStats: PlayerStatsWithId[]
): Record<string, boolean> => {
  const result: Record<string, boolean> = {}
  for (const entry of playerStats) {
    if (entry.hasEventData) result[entry.playerId] = true
  }
  return result
}

/**
 * Whether a player has data for a tab.
 *
 * Graceful fallbacks (so the carousel/tab-strip never wrongly empties):
 * - Nobody in the session carries a presence entry (older BE / still
 *   loading) → the signal is unavailable, gate nothing: available.
 * - Tab has no presence-backed columns (all deferred / not-yet-emitted) →
 *   available for everyone (show all) until the BE emits those metrics.
 *
 * But when OTHER players carry presence and this one has no entry, the BE
 * excluded them from the summary (roster-only player) — that's information,
 * not signal loss: NOT available. Without this distinction such players
 * pass every gate and surface all-dash tabs.
 *
 * Otherwise the player must have at least one of the tab's presence keys.
 */
export const hasTabData = (
  presenceByPlayer: PresenceByPlayer,
  playerId: string,
  tab: TabDef
): boolean => {
  const presence = presenceByPlayer[playerId]
  if (!presence) return Object.keys(presenceByPlayer).length === 0

  const presenceKeys = tab.columns
    .map((col) => accessorToPresenceKey(col.accessor))
    .filter((key): key is string => key !== null)

  if (presenceKeys.length === 0) return true

  return presenceKeys.some((key) => presence.has(key))
}

// ---------------------------------------------------------------------------
// Dev-only presence-key drift signal (interim guard)
// ---------------------------------------------------------------------------

export interface PresenceKeyDrift {
  /** BE emitted these presence keys, but no tab column resolves to them — a BE
   *  rename the FE accessor missed, or a metric the FE doesn't surface. */
  unmappedByFe: string[]
  /** Tab columns resolve to these presence keys, but the BE emitted them for no
   *  player this session. Often legitimate (nobody recorded the metric); if it
   *  recurs, the accessor is likely deferred (add to DEFERRED_ACCESSORS) or
   *  misnamed vs the BE key — the `defensiveAction*` class the static drift-guard can't catch. */
  notEmitted: string[]
}

/**
 * Compares the presence keys the backend actually emitted against the keys the
 * FE tab columns resolve to, to surface alias-map drift. Pure — returns the two
 * mismatch sets; the caller logs them (see the DEV-only effect in
 * `player-detail/index.tsx`). Returns empty sets when the session carries no
 * presence at all (older BE / no data) — nothing to compare.
 *
 * Best-effort interim guard: `notEmitted` can't distinguish a missing
 * classification from a metric simply not recorded this session. The reliable
 * fix is a backend-owned presence-key manifest the FE tests against in CI.
 */
export const detectPresenceKeyDrift = (
  presenceByPlayer: PresenceByPlayer,
  tabGroups: TabGroupDef[]
): PresenceKeyDrift => {
  const emitted = new Set<string>()
  for (const keys of Object.values(presenceByPlayer))
    for (const key of keys) emitted.add(key)
  if (emitted.size === 0) return { unmappedByFe: [], notEmitted: [] }

  const feKeys = new Set<string>()
  for (const group of tabGroups)
    for (const tab of group.tabs)
      for (const col of tab.columns) {
        const key = accessorToPresenceKey(col.accessor)
        if (key) feKeys.add(key)
      }

  return {
    unmappedByFe: [...emitted].filter((k) => !feKeys.has(k)),
    notEmitted: [...feKeys].filter((k) => !emitted.has(k))
  }
}
